// PROTOTYPE — three shapes for what a queued and in-flight Design Operation renders.
// Wayfinder ticket #964, map #942.
//
// Two surfaces, one question each:
//
//   * the Thread Queue pending card, above the composer
//   * the conversation bubble for the message the operation sent
//
// #929 built this derivation for Brain and deliberately left Design out:
// `summarizeThreadQueueOperation` returns `replacesPrompt: false` for every
// Design action, so a Design bubble still renders the arranged prompt that
// `formatDesignOperationPrompt` wrote — wiki-style instructions included.
//
//   a — Arranged prompt (today): the card is the operation's name and nothing
//       else; the bubble is the arranged prompt, verbatim. The baseline the
//       other two have to beat, shown honestly rather than described.
//   b — Operation-first: Brain's shape, Design-typed. One derivation at two
//       densities — headline, `$skill` chip, then a labelled item list (target,
//       brief, evidence). The card elides to three items; the bubble draws them
//       whole. Create and Rename relabel the item rather than call a name a brief.
//   c — Brief-first: the operation is chrome, not the message. A tinted strip
//       across the bubble's top edge carries icon, operation and `@target` —
//       the composer's headline bar, relocated — and the body underneath is the
//       user's own brief as prose, unlabelled. Create and Rename put the name
//       in the strip and drop the body entirely.
//
// Switcher toggles, because #964 asks two sub-questions that are states, not shapes:
//
//   * **State** cycles Queued → Running → Awaiting input. "Awaiting input" is the
//     Skill Bundle grilling mid-turn through a user-input attention request, which
//     is the only thing an in-flight Design Operation does that Brain's does not.
//   * **Sent** reveals a `What was sent` disclosure under every derived bubble,
//     carrying the arranged prompt verbatim. It is the escape hatch for
//     "native conversations stay native"; the question is whether it is needed.
//
// Throwaway: no persistence, no real submission, no tests. Scene data is a
// hand-written Design thread covering the awkward cases — Create (text is a
// name), Rename (text is a new name), Add Example (attachments plus an evidence
// URL folded into the brief), and the shipped `design.delete.reported` row,
// which the bubble variants have to sit next to without clashing.
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  FilePlus2Icon,
  ImageIcon,
  ImagePlusIcon,
  LinkIcon,
  Loader2Icon,
  PaletteIcon,
  PencilLineIcon,
  SparklesIcon,
  TagIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";

import type { DesignActionId, DesignOperationSubmission } from "@afar-research/contracts";
import { describeDesignComposerOperation, formatDesignOperationPrompt } from "@afar-research/afar";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";

/**
 * Every submission this prototype renders is agent-backed. Delete never becomes
 * a queued submission at all (#955) — its report is its own timeline row, drawn
 * below verbatim from the shipped one — so nothing here has a Delete case.
 *
 * Declared locally rather than imported so the prototype compiles against the
 * branch point as well as the working tree it was written in.
 */
type DesignAgentOperationSubmission = Exclude<
  DesignOperationSubmission,
  { readonly action: "delete-design-system" }
>;

/* -------------------------------------------------------------------------- */
/* Tints — copied from the Design composer, which is where they ship      */
/* -------------------------------------------------------------------------- */

interface Tint {
  readonly text: string;
  readonly surface: string;
  readonly border: string;
}

const LIBRARY_TINT: Tint = {
  text: "text-violet-800 dark:text-violet-200",
  surface: "bg-violet-500/10",
  border: "border-violet-500/35",
};
const IDENTITY_TINT: Tint = {
  text: "text-sky-800 dark:text-sky-200",
  surface: "bg-sky-500/10",
  border: "border-sky-500/35",
};
const DESTRUCTIVE_TINT: Tint = {
  text: "text-rose-800 dark:text-rose-200",
  surface: "bg-rose-500/10",
  border: "border-rose-500/40",
};

const TINT: Readonly<Record<DesignActionId, Tint>> = {
  "create-design": LIBRARY_TINT,
  "update-design": LIBRARY_TINT,
  "add-design-template": LIBRARY_TINT,
  "add-design-example": LIBRARY_TINT,
  "create-design-sample": LIBRARY_TINT,
  "rename-design-system": IDENTITY_TINT,
  "delete-design-system": DESTRUCTIVE_TINT,
};

const ICON: Readonly<Record<DesignActionId, typeof PaletteIcon>> = {
  "create-design": PaletteIcon,
  "update-design": PencilLineIcon,
  "add-design-template": FilePlus2Icon,
  "add-design-example": ImagePlusIcon,
  "create-design-sample": SparklesIcon,
  "rename-design-system": TagIcon,
  "delete-design-system": Trash2Icon,
};

/** The Skill Bundle each agent-backed operation's send invokes. */
const SKILL_BUNDLE: Readonly<Record<DesignActionId, string | null>> = {
  "create-design": "create-design",
  "update-design": "update-design",
  "add-design-template": "add-design-template",
  "add-design-example": "add-design-example",
  "create-design-sample": "create-design-sample",
  "rename-design-system": "rename-design-system",
  "delete-design-system": null,
};

/* -------------------------------------------------------------------------- */
/* The derivation the variants argue about                                     */
/* -------------------------------------------------------------------------- */

interface SummaryItem {
  readonly id: string;
  /** `null` on c, which never labels; b always labels. */
  readonly label: string;
  readonly text: string;
}

interface DesignSummary {
  readonly label: string;
  readonly skillBundleId: string | null;
  /** The `@` mention, for c's strip and b's first item. */
  readonly targetName: string | null;
  readonly targetPath: string | null;
  /** What the user actually typed. Create and Rename type a name, not a brief. */
  readonly body: { readonly kind: "brief" | "name" | "rename"; readonly text: string } | null;
  readonly items: ReadonlyArray<SummaryItem>;
}

/**
 * What #964 would add to `summarizeThreadQueueOperation`. Written here rather
 * than in `packages/afar` because the shape is the thing under judgement — and
 * because a is the case where none of it exists.
 */
function summarizeDesign(operation: DesignAgentOperationSubmission): DesignSummary {
  const label = describeDesignComposerOperation(operation.action).label;
  const skillBundleId = SKILL_BUNDLE[operation.action];
  const target = "target" in operation ? operation.target : null;
  const brief = "brief" in operation ? (operation.brief ?? null) : null;

  // Add Example folds evidence URLs into the brief as `Evidence URL:` lines
  // (#960). Splitting them back out is the whole argument for an item list.
  const briefLines = (brief ?? "").split("\n");
  const evidence = briefLines
    .filter((line) => line.startsWith("Evidence URL:"))
    .map((line) => line.slice("Evidence URL:".length).trim());
  const prose = briefLines
    .filter((line) => !line.startsWith("Evidence URL:"))
    .join("\n")
    .trim();

  const items: SummaryItem[] = [];
  if (target !== null) items.push({ id: "target", label: "Design system", text: target.name });

  if (operation.action === "create-design") {
    items.push({ id: "name", label: "Name", text: operation.displayName });
    return {
      label,
      skillBundleId,
      targetName: null,
      targetPath: null,
      body: { kind: "name", text: operation.displayName },
      items,
    };
  }
  if (operation.action === "rename-design-system") {
    items.push({ id: "next", label: "New name", text: operation.nextDisplayName ?? "" });
    return {
      label,
      skillBundleId,
      targetName: target?.name ?? null,
      targetPath: target?.path ?? null,
      body: { kind: "brief", text: operation.nextDisplayName ?? "" },
      items,
    };
  }
  if (prose.length > 0) items.push({ id: "brief", label: "Brief", text: prose });
  for (const [index, url] of evidence.entries()) {
    items.push({ id: `evidence:${index}`, label: "Evidence", text: url });
  }
  return {
    label,
    skillBundleId,
    targetName: target?.name ?? null,
    targetPath: target?.path ?? null,
    body: prose.length > 0 ? { kind: "brief", text: prose } : null,
    items,
  };
}

/* -------------------------------------------------------------------------- */
/* Scene                                                                       */
/* -------------------------------------------------------------------------- */

const ACME = {
  type: "design-system",
  name: "Acme Web",
  path: "design-system/acme-web/DESIGN.md",
} as const;

interface Attachment {
  readonly id: string;
  readonly name: string;
}

interface SentOperation {
  readonly id: string;
  readonly submission: DesignAgentOperationSubmission;
  readonly attachments: ReadonlyArray<Attachment>;
  readonly reply: string;
}

const SENT: ReadonlyArray<SentOperation> = [
  {
    id: "m1",
    submission: { action: "create-design", displayName: "Acme Web" },
    attachments: [],
    reply: "Wrote design-system/acme-web/DESIGN.md. Name Acme Web, slug acme-web. Left unstaged.",
  },
  {
    id: "m2",
    submission: {
      action: "update-design",
      target: ACME,
      brief:
        "Tighten the colour tokens. We have three greys doing the same job and DESIGN.md still points at --surface-2, which nothing renders any more.",
    },
    attachments: [],
    reply:
      "Collapsed --grey-200/--surface-2/--panel into --surface-raised and rewrote the three references. Slug identity preserved.",
  },
  {
    id: "m3",
    submission: {
      action: "add-design-example",
      target: ACME,
      brief:
        "The checkout after the Q3 redesign — the sticky summary rail is the part worth keeping.\nEvidence URL: https://acme.example.com/checkout",
    },
    attachments: [
      { id: "a1", name: "checkout-hero.png" },
      { id: "a2", name: "checkout-cart.png" },
    ],
    reply:
      "What should this example be called? I'd suggest `checkout-summary-rail` — it names the thing you pointed at rather than the page.",
  },
  {
    id: "m4",
    submission: {
      action: "rename-design-system",
      target: ACME,
      nextDisplayName: "Acme Design Language",
    },
    attachments: [],
    reply:
      "Moved to design-system/acme-design-language/, rewrote DESIGN.md preserving id, and followed designSystemSlug through 14 artifacts. Landed on slug acme-design-language.",
  },
];

interface QueuedOperation {
  readonly id: string;
  readonly submission: DesignAgentOperationSubmission;
}

const QUEUED: ReadonlyArray<QueuedOperation> = [
  {
    id: "q1",
    submission: {
      action: "create-design-sample",
      target: ACME,
      brief: "A pricing page that uses nothing but tokens from the system.",
    },
  },
  { id: "q2", submission: { action: "add-design-template", target: ACME } },
  {
    id: "q3",
    submission: {
      action: "update-design",
      target: ACME,
      brief:
        "Document the spacing scale. Right now it is four numbers in someone's head and a comment in the checkout example.",
    },
  },
];

/** The shipped card limit, `THREAD_QUEUE_OPERATION_CARD_ITEM_LIMIT`. */
const CARD_ITEM_LIMIT = 3;

type SceneState = "queued" | "running" | "awaiting-input";

interface Scene {
  readonly state: SceneState;
  readonly showArranged: boolean;
}

/* -------------------------------------------------------------------------- */
/* Shared chrome (identical in all three variants — only the card and the      */
/* bubble body change)                                                         */
/* -------------------------------------------------------------------------- */

function TargetChip({ name, path }: { name: string; path: string }) {
  return (
    <span
      title={path}
      className="inline-flex max-w-64 items-center gap-1.5 rounded-full border border-violet-500/25 bg-violet-500/10 px-2.5 py-1 text-xs font-medium text-violet-700 dark:text-violet-300"
    >
      <PaletteIcon className="size-3.5 shrink-0" />
      <span className="truncate">{name}</span>
    </span>
  );
}

/**
 * The Design System Mention chip the shipped bubble already draws above a user
 * message. a and b keep it; c folds the target into its strip instead, which is
 * the clearest single difference between them.
 */
function MentionRow({ summary }: { summary: DesignSummary }) {
  if (summary.targetName === null || summary.targetPath === null) return null;
  return (
    <div className="mb-2 flex flex-wrap gap-1.5">
      <TargetChip name={summary.targetName} path={summary.targetPath} />
    </div>
  );
}

function AttachmentChips({ attachments }: { attachments: ReadonlyArray<Attachment> }) {
  if (attachments.length === 0) return null;
  return (
    <div className="mb-2 flex flex-wrap gap-1.5">
      {attachments.map((attachment) => (
        <span
          key={attachment.id}
          className="inline-flex items-center gap-1.5 rounded-lg border bg-background/70 px-2 py-1 text-xs text-muted-foreground"
        >
          <ImageIcon className="size-3.5" />
          {attachment.name}
        </span>
      ))}
    </div>
  );
}

function Bubble({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[min(42rem,85%)] rounded-2xl border bg-muted/40 px-3.5 py-2.5">
        {children}
      </div>
    </div>
  );
}

function AssistantLine({ text }: { text: string }) {
  return (
    <div className="flex justify-start">
      <div className="max-w-[min(42rem,85%)] px-1 text-sm text-foreground/90">{text}</div>
    </div>
  );
}

function ArrangedPromptDisclosure({ operation }: { operation: DesignAgentOperationSubmission }) {
  return (
    <details className="mt-2">
      <summary className="cursor-pointer text-xs text-muted-foreground">What was sent</summary>
      <pre className="mt-1.5 max-h-56 overflow-auto rounded-md border bg-background/70 p-2 font-mono text-[11px] leading-relaxed whitespace-pre-wrap">
        {formatDesignOperationPrompt(operation)}
      </pre>
    </details>
  );
}

/** The shipped `design.delete.reported` row, so the variants are judged beside it. */
function DeleteReportRow() {
  return (
    <div className="flex justify-end">
      <div className="max-w-[min(42rem,90%)] rounded-2xl border border-destructive/35 bg-destructive/5 px-3.5 py-2.5">
        <div className="grid gap-1.5 text-sm">
          <p className="font-medium">Deleted 1 Design System</p>
          <ul className="grid gap-0.5">
            <li className="break-words text-muted-foreground">
              Acme Mobile (acme-mobile) — 23 files
            </li>
          </ul>
          <p className="text-xs text-muted-foreground">
            Removed from disk and left unstaged. Committed work comes back with git.
          </p>
        </div>
      </div>
    </div>
  );
}

/** The ordinary mid-turn user-input attention request — not Design-specific. */
function PendingUserInputCard() {
  return (
    <div className="flex justify-start">
      <div className="max-w-[min(42rem,85%)] rounded-2xl border border-amber-500/40 bg-amber-500/5 px-3.5 py-3">
        <p className="text-sm font-medium">What should this example be called?</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {["checkout-summary-rail", "q3-checkout", "Something else…"].map((option) => (
            <span key={option} className="rounded-lg border bg-background px-2.5 py-1 text-xs">
              {option}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Variant a — Arranged prompt (today)                                         */
/* -------------------------------------------------------------------------- */

function CardA(_props: { operation: DesignAgentOperationSubmission }) {
  // Today: `items` is empty and there is no blurb, so the body renders nothing.
  // The card is its title line and a blank space where Brain shows its targets.
  return null;
}

function BubbleA({ sent }: { sent: SentOperation; scene: Scene }) {
  return (
    <Bubble>
      <MentionRow summary={summarizeDesign(sent.submission)} />
      <AttachmentChips attachments={sent.attachments} />
      <p className="whitespace-pre-wrap font-mono text-[12.5px] leading-relaxed">
        {formatDesignOperationPrompt(sent.submission)}
      </p>
    </Bubble>
  );
}

/* -------------------------------------------------------------------------- */
/* Variant b — Operation-first (Brain's shape, Design-typed)                   */
/* -------------------------------------------------------------------------- */

function CardB({ operation }: { operation: DesignAgentOperationSubmission }) {
  const summary = summarizeDesign(operation);
  const shown = summary.items.slice(0, CARD_ITEM_LIMIT);
  const hidden = Math.max(0, summary.items.length - CARD_ITEM_LIMIT);
  if (shown.length === 0) return null;
  return (
    <ul className="mt-1 grid gap-0.5 text-sm">
      {shown.map((item) => (
        <li key={item.id} className="truncate" title={item.text}>
          <span className="text-muted-foreground">{item.label} — </span>
          {item.text}
        </li>
      ))}
      {hidden > 0 ? <li className="text-muted-foreground">+{hidden} more</li> : null}
    </ul>
  );
}

function BubbleB({ sent, scene }: { sent: SentOperation; scene: Scene }) {
  const summary = summarizeDesign(sent.submission);
  return (
    <Bubble>
      <MentionRow summary={summary} />
      <AttachmentChips attachments={sent.attachments} />
      <div className="grid gap-1.5 text-sm">
        <p className="flex flex-wrap items-center gap-1.5 font-medium">
          {summary.label}
          {summary.skillBundleId === null ? null : (
            <span className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-xs text-primary">
              ${summary.skillBundleId}
            </span>
          )}
        </p>
        <ul className="grid gap-0.5">
          {summary.items.map((item) => (
            <li key={item.id} className="break-words">
              <span className="text-muted-foreground">{item.label} — </span>
              {item.text}
            </li>
          ))}
        </ul>
        {scene.showArranged ? <ArrangedPromptDisclosure operation={sent.submission} /> : null}
      </div>
    </Bubble>
  );
}

/* -------------------------------------------------------------------------- */
/* Variant c — Brief-first (the operation is chrome)                           */
/* -------------------------------------------------------------------------- */

function OperationStrip({
  action,
  targetName,
  targetPath,
  compact,
}: {
  action: DesignActionId;
  targetName: string | null;
  targetPath: string | null;
  compact?: boolean;
}) {
  const Icon = ICON[action];
  const tint = TINT[action];
  const label = describeDesignComposerOperation(action).label;
  return (
    <div
      className={cn(
        "flex items-center gap-1.5 border-b px-3 py-1.5 text-xs font-medium",
        tint.surface,
        tint.border,
        tint.text,
        compact && "px-2 py-1",
      )}
    >
      <Icon className="size-3.5 shrink-0" />
      <span className="truncate">{label}</span>
      {targetName === null ? null : (
        <>
          <span className="opacity-50">→</span>
          <span className="truncate opacity-90" title={targetPath ?? undefined}>
            @{targetName}
          </span>
        </>
      )}
    </div>
  );
}

function CardC({ operation }: { operation: DesignAgentOperationSubmission }) {
  const summary = summarizeDesign(operation);
  return (
    <div className="mt-1.5 overflow-hidden rounded-lg border">
      <OperationStrip
        action={operation.action}
        targetName={summary.targetName}
        targetPath={summary.targetPath}
        compact
      />
      {summary.body === null ? null : (
        <p
          className={cn(
            "line-clamp-2 px-2 py-1.5 text-sm",
            summary.body.kind === "brief" ? "" : "font-medium",
          )}
        >
          {summary.body.kind === "rename" ? `→ ${summary.body.text}` : summary.body.text}
        </p>
      )}
    </div>
  );
}

function BubbleC({ sent, scene }: { sent: SentOperation; scene: Scene }) {
  const summary = summarizeDesign(sent.submission);
  const evidence = summary.items.filter((item) => item.id.startsWith("evidence:"));
  return (
    <div className="flex justify-end">
      <div className="max-w-[min(42rem,85%)] overflow-hidden rounded-2xl border bg-muted/40">
        <OperationStrip
          action={sent.submission.action}
          targetName={summary.targetName}
          targetPath={summary.targetPath}
        />
        <div className="px-3.5 py-2.5">
          <AttachmentChips attachments={sent.attachments} />
          {summary.body === null ? (
            <p className="text-sm text-muted-foreground italic">No brief — the skill will ask.</p>
          ) : summary.body.kind === "brief" ? (
            <p className="whitespace-pre-wrap text-sm">{summary.body.text}</p>
          ) : (
            <p className="text-sm">
              {summary.body.kind === "rename" ? (
                <>
                  <span className="text-muted-foreground line-through">{summary.targetName}</span>
                  <span className="mx-1.5 text-muted-foreground">→</span>
                  <span className="font-medium">{summary.body.text}</span>
                </>
              ) : (
                <span className="font-medium">{summary.body.text}</span>
              )}
            </p>
          )}
          {evidence.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {evidence.map((item) => (
                <span
                  key={item.id}
                  className="inline-flex items-center gap-1.5 rounded-lg border bg-background/70 px-2 py-1 text-xs text-muted-foreground"
                >
                  <LinkIcon className="size-3.5" />
                  {item.text}
                </span>
              ))}
            </div>
          ) : null}
          {scene.showArranged ? <ArrangedPromptDisclosure operation={sent.submission} /> : null}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Surfaces                                                                    */
/* -------------------------------------------------------------------------- */

interface VariantParts {
  readonly key: string;
  readonly name: string;
  readonly Card: (props: { operation: DesignAgentOperationSubmission }) => React.ReactNode;
  readonly Bubble: (props: { sent: SentOperation; scene: Scene }) => React.ReactNode;
}

const VARIANTS: ReadonlyArray<VariantParts> = [
  { key: "a", name: "Arranged prompt (today)", Card: CardA, Bubble: BubbleA },
  { key: "b", name: "Operation-first", Card: CardB, Bubble: BubbleB },
  { key: "c", name: "Brief-first", Card: CardC, Bubble: BubbleC },
];

/** The real `ThreadQueuePendingStack` chrome, with only the card body swapped. */
function PendingStack({ variant, scene }: { variant: VariantParts; scene: Scene }) {
  const running = scene.state === "queued" ? null : (QUEUED[0] ?? null);
  const pending = scene.state === "queued" ? QUEUED : QUEUED.slice(1);
  return (
    <div className="overflow-hidden rounded-xl border border-border/70 bg-background/96 shadow-[0_8px_24px_rgba(0,0,0,0.06)]">
      <div className="flex items-center justify-between gap-3 border-b border-border/60 px-3 py-2">
        <div>
          <p className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
            Thread Queue
          </p>
          <p className="text-sm font-medium">
            {pending.length} queued
            {running === null
              ? ""
              : ` · ${describeDesignComposerOperation(running.submission.action).label} running`}
          </p>
        </div>
        <Button type="button" size="xs" variant="outline">
          Clear pending
        </Button>
      </div>
      <ol className="max-h-56 divide-y divide-border/60 overflow-y-auto">
        {pending.map((item) => (
          <li key={item.id} className="px-3 py-2.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-muted-foreground">
                  {describeDesignComposerOperation(item.submission.action).label}
                </p>
                <variant.Card operation={item.submission} />
              </div>
              <Button type="button" size="xs" variant="ghost" aria-label="Remove">
                <XIcon />
              </Button>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Transcript({ variant, scene }: { variant: VariantParts; scene: Scene }) {
  const running = scene.state === "queued" ? null : (QUEUED[0] ?? null);
  return (
    <div className="mx-auto grid max-w-3xl gap-4 px-4 py-6">
      {SENT.map((sent, index) => (
        <div key={sent.id} className="grid gap-3">
          <variant.Bubble sent={sent} scene={scene} />
          {index === SENT.length - 1 && scene.state === "awaiting-input" ? (
            <PendingUserInputCard />
          ) : (
            <AssistantLine text={sent.reply} />
          )}
        </div>
      ))}
      <DeleteReportRow />
      {running === null ? null : (
        <div className="grid gap-3">
          <variant.Bubble
            sent={{ id: running.id, submission: running.submission, attachments: [], reply: "" }}
            scene={scene}
          />
          <div className="flex items-center gap-2 px-1 text-sm text-muted-foreground">
            <Loader2Icon className="size-3.5 animate-spin" />
            Working…
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Switcher                                                                    */
/* -------------------------------------------------------------------------- */

const STATE_ORDER: ReadonlyArray<SceneState> = ["queued", "running", "awaiting-input"];
const STATE_LABEL: Readonly<Record<SceneState, string>> = {
  queued: "Queued",
  running: "Running",
  "awaiting-input": "Awaiting input",
};

function PrototypeSwitcher({
  current,
  scene,
  onChange,
  onCycleState,
  onToggleArranged,
}: {
  current: string;
  scene: Scene;
  onChange: (next: string) => void;
  onCycleState: () => void;
  onToggleArranged: () => void;
}) {
  const index = Math.max(
    0,
    VARIANTS.findIndex((variant) => variant.key === current),
  );
  const active = VARIANTS[index] ?? VARIANTS[0]!;
  const cycle = (delta: number) => {
    const next = VARIANTS[(index + delta + VARIANTS.length) % VARIANTS.length];
    if (next) onChange(next.key);
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
      ) {
        return;
      }
      if (event.key === "ArrowLeft") cycle(-1);
      if (event.key === "ArrowRight") cycle(1);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  if (import.meta.env.PROD) return null;

  return (
    <div className="fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-1 rounded-full bg-neutral-900 px-1.5 py-1.5 text-white shadow-lg ring-1 ring-black/20">
      <button
        aria-label="Previous variant"
        className="flex size-7 cursor-pointer items-center justify-center rounded-full hover:bg-white/15"
        onClick={() => cycle(-1)}
        type="button"
      >
        <ChevronLeftIcon className="size-4" />
      </button>
      <span className="px-2 text-xs font-medium">
        {active.key.toUpperCase()} — {active.name}
      </span>
      <button
        aria-label="Next variant"
        className="flex size-7 cursor-pointer items-center justify-center rounded-full hover:bg-white/15"
        onClick={() => cycle(1)}
        type="button"
      >
        <ChevronRightIcon className="size-4" />
      </button>
      <span className="mx-1 h-4 w-px bg-white/25" />
      <button
        className="cursor-pointer rounded-full px-2.5 py-1 text-xs font-medium hover:bg-white/15"
        onClick={onCycleState}
        type="button"
      >
        {STATE_LABEL[scene.state]}
      </button>
      <button
        className={cn(
          "cursor-pointer rounded-full px-2.5 py-1 text-xs font-medium",
          scene.showArranged ? "bg-white text-neutral-900" : "hover:bg-white/15",
        )}
        onClick={onToggleArranged}
        type="button"
      >
        Sent
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Route component                                                             */
/* -------------------------------------------------------------------------- */

export function DesignOperationRenderingPrototype() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { readonly variant?: string };
  const variantKey = search.variant ?? "a";
  const [state, setState] = useState<SceneState>("queued");
  const [showArranged, setShowArranged] = useState(false);
  const scene: Scene = { state, showArranged };

  const variant = useMemo(
    () => VARIANTS.find((item) => item.key === variantKey) ?? VARIANTS[0]!,
    [variantKey],
  );

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <header className="flex shrink-0 items-center gap-2 border-b px-4 py-2.5">
        <PaletteIcon className="size-4 text-muted-foreground" />
        <span className="text-sm font-medium">Design</span>
        <span className="text-xs text-muted-foreground">
          afar-research-app · queued and in-flight Design Operations
        </span>
        <span className="ml-auto rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300">
          PROTOTYPE
        </span>
      </header>

      <div className="min-h-0 flex-1 overflow-auto">
        <Transcript variant={variant} scene={scene} />
      </div>

      <div className="shrink-0 px-4 pb-20">
        <div className="mx-auto w-full max-w-3xl">
          <PendingStack variant={variant} scene={scene} />
          {/* The composer itself is not under judgement here — #961 settled it. */}
          <div className="mt-2 rounded-xl border border-violet-500/30 bg-background">
            <div className="flex items-center gap-1.5 rounded-t-xl border-b border-violet-500/35 bg-violet-500/10 px-3 py-1.5 text-xs font-medium text-violet-800 dark:text-violet-200">
              <PencilLineIcon className="size-3.5" />
              Update a Design System
              <span className="opacity-70">
                — Revises DESIGN.md while preserving its storage identity.
              </span>
            </div>
            <div className="px-3 py-3 text-sm text-muted-foreground">
              What should change about @the Design System…
            </div>
          </div>
        </div>
      </div>

      <PrototypeSwitcher
        current={variantKey}
        scene={scene}
        onChange={(next) =>
          void navigate({ search: { variant: next }, to: "/design-operation-rendering-prototype" })
        }
        onCycleState={() =>
          setState(
            (current) =>
              STATE_ORDER[(STATE_ORDER.indexOf(current) + 1) % STATE_ORDER.length] ?? "queued",
          )
        }
        onToggleArranged={() => setShowArranged((current) => !current)}
      />
    </div>
  );
}
